import React, { useState } from 'react';
import { Send, Server, Target, Workflow, Check, CalendarDays, ChevronDown, Inbox } from 'lucide-react';
import { services, siteConfig } from '../data/mock';

const icons = { Send, Server, Target, Workflow };

const ThreadDetail = ({ s }) => {
  const Icon = icons[s.icon];
  return (
    <div className="p-6 sm:p-8 pop-in" key={s.id} data-testid={`service-detail-${s.id}`}>
      <div className="flex items-start gap-4 mb-6">
        <span className="w-12 h-12 rounded-xl bg-ink text-white flex items-center justify-center flex-shrink-0"><Icon className="w-5 h-5" /></span>
        <div>
          <p className="font-mono text-[11px] uppercase tracking-widest text-slate-2 mb-1">{s.folder} · from Usama</p>
          <h3 className="font-display text-xl sm:text-2xl font-bold text-ink">{s.title}</h3>
        </div>
      </div>
      <p className="text-slate leading-relaxed mb-7">{s.summary}</p>

      <p className="font-mono text-[11px] uppercase tracking-widest text-slate-2 mb-3">What you get</p>
      <ul className="grid sm:grid-cols-2 gap-2.5 mb-7">
        {s.deliverables.map((d) => (
          <li key={d} className="flex items-start gap-2.5 text-sm text-ink">
            <span className="mt-0.5 w-4 h-4 rounded-full bg-[#ECFDF5] flex items-center justify-center flex-shrink-0"><Check className="w-3 h-3 text-green" /></span>
            {d}
          </li>
        ))}
      </ul>

      <div className="grid sm:grid-cols-3 gap-3 mb-7">
        {s.specs.map(([k, v]) => (
          <div key={k} className="rounded-xl bg-paper-2 border border-[#E2E8F0] px-4 py-3">
            <p className="font-mono text-[10px] uppercase tracking-widest text-slate-2">{k}</p>
            <p className="text-sm font-medium text-ink mt-1">{v}</p>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-3">
        <a href={siteConfig.calendly} target="_blank" rel="noopener noreferrer" className="btn-green !h-11 text-sm" data-testid={`service-book-${s.id}`}>
          <CalendarDays className="w-4 h-4" /> Discuss this on a call
        </a>
        <a href="#contact" className="btn-outline !h-11 text-sm" data-testid={`service-message-${s.id}`}>Send a message</a>
      </div>
    </div>
  );
};

const ServicesSection = () => {
  const [active, setActive] = useState(services[0].id);
  const current = services.find((s) => s.id === active);

  return (
    <section id="services" className="py-24 lg:py-32" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="max-w-3xl mb-12" data-reveal>
          <p className="eyebrow mb-4">Services · Inbox</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold text-ink leading-[1.05] mb-4">
            Four threads. One goal: <span className="mark">more qualified replies.</span>
          </h2>
          <p className="text-slate text-base md:text-lg leading-relaxed">
            Everything I offer sits between "who should we email?" and a booked meeting. Open a thread to see what's included.
          </p>
        </div>

        <div className="window" data-reveal>
          <div className="window-bar">
            <span className="window-dot" /><span className="window-dot" /><span className="window-dot" />
            <span className="ml-3 flex items-center gap-2 font-mono text-[11px] text-slate-2 tracking-wider"><Inbox className="w-3.5 h-3.5" /> INBOX · {services.length} THREADS</span>
            <span className="ml-auto pill pill-green">{services.length} unread</span>
          </div>

          <div className="grid lg:grid-cols-12">
            <div className="lg:col-span-5 lg:border-r border-[#E2E8F0]" role="tablist" data-testid="service-thread-list">
              {services.map((s) => {
                const Icon = icons[s.icon];
                const isActive = s.id === active;
                return (
                  <div key={s.id}>
                    <button
                      role="tab"
                      aria-selected={isActive}
                      onClick={() => setActive(s.id)}
                      className={`thread-row ${isActive ? 'active' : ''}`}
                      data-testid={`service-thread-${s.id}`}
                    >
                      <span className={`mt-0.5 w-9 h-9 rounded-lg flex items-center justify-center ${isActive ? 'bg-ink text-white' : 'bg-paper-2 text-ink'}`}>
                        <Icon className="w-4 h-4" />
                      </span>
                      <span className="min-w-0">
                        <span className="flex items-center gap-2 mb-0.5">
                          <span className="font-semibold text-ink text-[15px]">{s.title}</span>
                          {!isActive && <span className="w-2 h-2 rounded-full bg-green flex-shrink-0" />}
                        </span>
                        <span className="block text-sm text-slate leading-snug line-clamp-2">{s.preview}</span>
                      </span>
                      <span className="flex flex-col items-end gap-2">
                        <span className="pill !text-[10px]">{s.folder}</span>
                        <ChevronDown className={`w-4 h-4 text-slate-2 lg:hidden transition-transform ${isActive ? 'rotate-180' : ''}`} />
                      </span>
                    </button>
                    {isActive && <div className="lg:hidden border-b border-[#E2E8F0] bg-white"><ThreadDetail s={s} /></div>}
                  </div>
                );
              })}
            </div>
            <div className="hidden lg:block lg:col-span-7 bg-white">
              <ThreadDetail s={current} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
